import React, { useState } from "react";
import { useSettings } from "../context/SettingsContext";
import { submitContact } from "../api";

export default function ContactPage() {
  const { getBanner, getContact } = useSettings();
  const contact = getContact();
  const bannerSrc = getBanner("contact");

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitContact(form);
      setSuccess(true);
      setForm({ fullName: "", email: "", phone: "", city: "", message: "" });
    } catch (err) {
      setError("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const INFO = [
    {
      label: "Call Us",
      value: contact.phone,
      href: `tel:${contact.phone}`,
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.14 10.7a19.79 19.79 0 01-3.07-8.67A2 2 0 012.25 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
        </svg>
      ),
    },
    {
      label: "Email Us",
      value: contact.email,
      href: `mailto:${contact.email}`,
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
    },
    {
      label: "Visit Us",
      value: contact.address,
      href: null,
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-[60px]">
      {/* Banner */}
      <div className="relative h-44 sm:h-56 md:h-72 overflow-hidden">
        <img
          src={bannerSrc || "images/bg1.png"}
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <h1
            className="font-display text-white font-light"
            style={{ fontSize: "clamp(2.2rem, 5.5vw, 4rem)" }}
          >
            Contact Us
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-20 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20">
          {/* Left — info */}
          <div>
            <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
              Get In Touch
            </span>
            <h2
              className="font-display font-normal text-stone-900 leading-tight mb-5"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
            >
              Let's Start a Conversation
            </h2>
            <p className="font-body text-stone-500 text-base leading-relaxed mb-10">
              Whether you're looking to invest in a franchise or expand your
              brand, we're here to guide you every step of the way.
            </p>

            <div className="space-y-6">
              {INFO.map(
                (item) =>
                  item.value && (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <p className="font-body text-stone-400 text-xs tracking-widest uppercase mb-1">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="font-body text-stone-700 text-sm hover:text-gold transition-colors"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="font-body text-stone-700 text-sm">
                            {item.value}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
              )}
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-[#F4EEE6] rounded-xs rounded-br-[30px] p-6 md:p-8">
            {success ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#16a34a"
                    strokeWidth="2.5"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p className="font-display text-stone-800 text-xl mb-2">
                  Message Sent!
                </p>
                <p className="font-body text-stone-500 text-sm">
                  We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-6 font-body text-xs tracking-widest uppercase text-gold hover:text-stone-900 transition-colors"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  {
                    name: "fullName",
                    label: "Full Name *",
                    type: "text",
                    placeholder: "Your full name",
                    required: true,
                  },
                  {
                    name: "email",
                    label: "Email Address *",
                    type: "email",
                    placeholder: "your@email.com",
                    required: true,
                  },
                  {
                    name: "phone",
                    label: "Phone Number *",
                    type: "tel",
                    placeholder: "+91 99999 99999",
                    required: true,
                  },
                  {
                    name: "city",
                    label: "City",
                    type: "text",
                    placeholder: "Your city",
                    required: false,
                  },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="font-body text-stone-600 text-xs tracking-wide block mb-1.5">
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      name={f.name}
                      required={f.required}
                      value={form[f.name]}
                      onChange={handleChange}
                      placeholder={f.placeholder}
                      className="w-full font-body text-stone-800 text-sm px-4 py-3 rounded-xl border border-stone-200 focus:border-gold focus:ring-1 focus:ring-gold/20 outline-none transition-all duration-200 bg-white"
                    />
                  </div>
                ))}

                <div>
                  <label className="font-body text-stone-600 text-xs tracking-wide block mb-1.5">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your franchise goals..."
                    className="w-full font-body text-stone-800 text-sm px-4 py-3 rounded-xl border border-stone-200 focus:border-gold focus:ring-1 focus:ring-gold/20 outline-none transition-all duration-200 bg-white"
                  />
                </div>

                {error && (
                  <p className="font-body text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-[#A07830] disabled:opacity-60 disabled:cursor-not-allowed text-white font-body text-[11px] font-semibold tracking-[0.2em] uppercase py-4 rounded-xs rounded-br-[30px] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
